import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface BlogPost {
  id: number;
  title: string;
  description: string;
  content: string;
  date: string;
  tags: string[];
}

const blogPosts: BlogPost[] = [
  {
    id: 1,
    title: "PHP 8.3 新特性详解",
    description: "深入了解PHP 8.3带来的新功能和改进",
    content: `PHP 8.3 带来了许多令人兴奋的新特性：

1. **类型化类常量**
   \`\`\`php
   class Config {
       public const string APP_NAME = 'MyApp';
       public const int MAX_USERS = 100;
   }
   \`\`\`

2. **动态获取类常量**
   \`\`\`php
   $className = 'MyClass';
   echo $className::CONSTANT_NAME;
   \`\`\`

3. **json_validate() 函数**
   \`\`\`php
   if (json_validate($jsonString)) {
       $data = json_decode($jsonString);
   }
   \`\`\`

这些新特性让PHP开发更加高效和安全！`,
    date: "2024-01-15",
    tags: ["PHP 8.3", "新特性", "类型系统"]
  },
  {
    id: 2,
    title: "Laravel 最佳实践指南",
    description: "提升你的Laravel应用质量和性能",
    content: `Laravel开发中的最佳实践：

**1. 使用服务容器**
\`\`\`php
// 好的做法
app(MyService::class)->doSomething();

// 更好的做法 - 依赖注入
public function __construct(private MyService $myService) {}
\`\`\`

**2. 数据库查询优化**
\`\`\`php
// 避免N+1查询问题
$users = User::with('posts')->get();
\`\`\`

**3. 使用队列处理耗时任务**
\`\`\`php
dispatch(new ProcessPodcast($podcast));
\`\`\`

**4. 表单验证**
\`\`\`php
$request->validate([
    'title' => 'required|max:255',
    'email' => 'required|email|unique:users'
]);
\`\`\``,
    date: "2024-01-10",
    tags: ["Laravel", "最佳实践", "性能优化"]
  },
  {
    id: 3,
    title: "PHP设计模式：单例模式",
    description: "深入理解并实现PHP中的单例模式",
    content: `单例模式确保一个类只有一个实例，并提供一个全局访问点。

**基本实现：**
\`\`\`php
class Database {
    private static ?self $instance = null;
    private array $connection;
    
    private function __construct() {
        $this->connection = ['host' => 'localhost', 'db' => 'myapp'];
    }
    
    public static function getInstance(): self {
        if (self::$instance === null) {
            self::$instance = new self();
        }
        return self::$instance;
    }
    
    public function query(string $sql): array {
        // 执行查询逻辑
        return ['result' => 'data'];
    }
    
    private function __clone() {}
    private function __wakeup() {}
}
\`\`\`

**使用示例：**
\`\`\`php
$db = Database::getInstance();
$results = $db->query("SELECT * FROM users");
\`\`\`

**优点：**
- 控制实例数量，节省资源
- 提供全局访问点
- 延迟初始化

**使用场景：**
- 数据库连接
- 日志记录器
- 配置管理器`,
    date: "2024-01-05",
    tags: ["设计模式", "单例模式", "OOP"]
  }
];

export function PHPBlog() {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  if (selectedPost) {
    return (
      <div className="max-w-4xl mx-auto">
        <Button 
          onClick={() => setSelectedPost(null)} 
          variant="outline" 
          className="mb-6"
        >
          ← 返回文章列表
        </Button>
        
        <article className="bg-card rounded-lg shadow-lg p-8">
          <header className="mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {selectedPost.title}
            </h1>
            <p className="text-muted-foreground mb-4">{selectedPost.description}</p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{selectedPost.date}</span>
              <div className="flex gap-2">
                {selectedPost.tags.map(tag => (
                  <span key={tag} className="bg-primary/10 text-primary px-2 py-1 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </header>
          
          <div className="prose prose-lg max-w-none">
            <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
              {selectedPost.content}
            </pre>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          编程经验分享
        </h1>
        <p className="text-xl text-muted-foreground">
          探索PHP开发的深度与广度，分享编程经验和最佳实践
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {blogPosts.map(post => (
          <Card 
            key={post.id} 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedPost(post)}
          >
            <CardHeader>
              <CardTitle className="text-xl">{post.title}</CardTitle>
              <CardDescription>{post.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{post.date}</span>
                <div className="flex gap-1">
                  {post.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}